import React, { useEffect, useMemo, useState } from "react";
import { Combobox, Row, TextInput } from "@narmi/design_system";
import {
  getCountryCallingCodeOptions,
  getCountryForInternationalPhoneNumber,
  parseInternationalPhoneNumber,
  type CountryCallingCodeOption,
} from "byzantine/src/dbbl/utils/internationalPhoneNumber";

export type InternationalPhoneTextInputProps = {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  countryCodeLabel?: string;
  phoneNumberLabel?: string;
  error?: string;
  disabled?: boolean;
  locale?: string;
  defaultCountryCode?: string;
  countryCodeTestId?: string;
  phoneNumberTestId?: string;
  endContent?: React.ReactNode;
};

type ComboboxItemElement = React.ReactElement<{
  searchValue?: string;
  value: string;
}>;

const getOptionSearchValue = (option: CountryCallingCodeOption) =>
  `${option.name} ${option.callingCode} ${option.code}`;

const getSelectedOptionInputValue = (option?: CountryCallingCodeOption) =>
  option ? `${option.flag} ${option.callingCode}` : "";

const getCountryCodeSearchQuery = (
  inputValue: string,
  selectedOption?: CountryCallingCodeOption
) => {
  const selectedInputValue = getSelectedOptionInputValue(selectedOption);
  return selectedInputValue && inputValue.startsWith(selectedInputValue)
    ? inputValue.slice(selectedInputValue.length).trim()
    : inputValue;
};

const getOptionFromInputValue = (options: CountryCallingCodeOption[], inputValue: string) =>
  options.find(
    (option) =>
      inputValue === option.label ||
      inputValue === option.value ||
      inputValue === getSelectedOptionInputValue(option) ||
      inputValue === getOptionSearchValue(option)
  );

const stripPhoneNumber = (value: string) => value.replace(/[^\d]/g, "");

const getDefaultCountryCode = (options: CountryCallingCodeOption[], defaultCountryCode: string) =>
  options.find((option) => option.code.toLowerCase() === defaultCountryCode.toLowerCase())?.code ||
  options[0]?.code;

const getCountryCodeFromCallingCode = (options: CountryCallingCodeOption[], callingCode: string) =>
  options.find((option) => option.callingCode === callingCode)?.code;

const formatSubmittedValue = (callingCode: string, phoneNumber: string) => {
  const strippedPhoneNumber = stripPhoneNumber(phoneNumber);
  return strippedPhoneNumber ? `${callingCode}${strippedPhoneNumber}` : "";
};

const InternationalPhoneTextInput = ({
  value = "",
  onChange,
  onBlur,
  countryCodeLabel = "Country code",
  phoneNumberLabel = "Phone number",
  error,
  disabled,
  locale = "en",
  defaultCountryCode = "US",
  countryCodeTestId,
  phoneNumberTestId,
  endContent,
}: InternationalPhoneTextInputProps) => {
  const options = useMemo(() => getCountryCallingCodeOptions(locale), [locale]);
  const parsedPhoneNumber = useMemo(() => parseInternationalPhoneNumber(value), [value]);
  const parsedCountryCode = useMemo(() => getCountryForInternationalPhoneNumber(value), [value]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    parsedCountryCode ||
      getCountryCodeFromCallingCode(options, parsedPhoneNumber.countryCallingCode) ||
      getDefaultCountryCode(options, defaultCountryCode)
  );
  const selectedOption = options.find((option) => option.code === selectedCountryCode);
  const [inputValue, setInputValue] = useState(getSelectedOptionInputValue(selectedOption));
  const [localPhoneNumber, setLocalPhoneNumber] = useState(parsedPhoneNumber.nationalNumber);

  useEffect(() => {
    if (parsedCountryCode) {
      setSelectedCountryCode(parsedCountryCode);
    }
  }, [parsedCountryCode]);

  useEffect(() => {
    setInputValue(getSelectedOptionInputValue(selectedOption));
  }, [selectedOption]);

  useEffect(() => {
    if (parsedPhoneNumber.nationalNumber) {
      setLocalPhoneNumber(parsedPhoneNumber.nationalNumber);
    } else if (!value) {
      setLocalPhoneNumber("");
    }
  }, [parsedPhoneNumber.nationalNumber, value]);

  const handleCountryCodeInputChange = (updatedValue: string) => {
    const matchingOption = getOptionFromInputValue(options, updatedValue);
    const searchQuery = getCountryCodeSearchQuery(updatedValue, selectedOption);
    setInputValue(matchingOption ? getSelectedOptionInputValue(matchingOption) : searchQuery);
  };

  const handleCountryCodeChange = (countryCode: string) => {
    const nextOption = options.find((option) => option.code === countryCode);
    if (!nextOption) {
      return;
    }
    setSelectedCountryCode(nextOption.code);
    setInputValue(getSelectedOptionInputValue(nextOption));
    onChange(formatSubmittedValue(nextOption.callingCode, localPhoneNumber));
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    setLocalPhoneNumber(rawValue);
    onChange(formatSubmittedValue(selectedOption?.callingCode || "", rawValue));
  };

  const handlePhoneNumberBlur = () => {
    onBlur?.(formatSubmittedValue(selectedOption?.callingCode || "", localPhoneNumber));
  };

  return (
    <div className="internationalPhoneTextInput">
      <div className="internationalPhoneTextInput__countryCode">
        <Combobox
          label={countryCodeLabel}
          onChange={handleCountryCodeChange}
          inputValue={inputValue}
          onInputChange={handleCountryCodeInputChange}
          filterItemsByInput={(items: ComboboxItemElement[], typedValue: string) => {
            const typedSearchQuery = getCountryCodeSearchQuery(typedValue, selectedOption);
            return items.filter((item) => {
              const query = item.props.searchValue || item.props.value || "";
              return query.toLowerCase().includes(typedSearchQuery.toLowerCase());
            });
          }}
          testId={countryCodeTestId}
        >
          {options.map((option) => (
            <Combobox.Item
              key={option.code}
              value={option.code}
              searchValue={getOptionSearchValue(option)}
            >
              <Row gapSize="xxs">
                <Row.Item shrink>{option.flag}</Row.Item>
                <Row.Item>{`${option.name} (${option.callingCode})`}</Row.Item>
              </Row>
            </Combobox.Item>
          ))}
        </Combobox>
      </div>
      <div className="internationalPhoneTextInput__phoneNumber">
        <TextInput
          label={phoneNumberLabel}
          onChange={handlePhoneNumberChange}
          onBlur={handlePhoneNumberBlur}
          value={localPhoneNumber}
          inputMode="numeric"
          error={error}
          disabled={disabled}
          testId={phoneNumberTestId}
          endContent={endContent}
        />
      </div>
    </div>
  );
};

export default InternationalPhoneTextInput;
